import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  RefreshControl,
  TouchableOpacity,
  Animated,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import Head from 'expo-router/head';
import { router, useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Newspaper, ArrowUp } from 'lucide-react-native';
import {
  useTheme,
  TYPOGRAPHY,
  SPACING,
  ELEVATION,
  BORDER_RADIUS,
  READING_MAX_WIDTH,
  SHELL_HEADER_HEIGHT,
  SHELL_RULE_WIDTH,
} from '../../src/theme';
import { ContentConstraint } from '@components/layout';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { PostCardSkeleton } from '@components/posts';
import {
  EmptyState,
  ErrorState,
  Avatar,
  RediLogo,
  useConfirm,
  SignInChip,
  DotLabel,
} from '@components/common';
import { useResponsiveLayout } from '@hooks/useResponsiveLayout';
import { useAuthGate } from '@hooks/useAuthGate';
import { usePostsStore } from '../../src/store/postsStore';
import { useAuthStore } from '../../src/store/authStore';
import { useUIStore } from '../../src/store/uiStore';
import { Post } from '../../src/types';
import { mergeOwnPosts } from '../../src/utils/postVisibility';
import { suppressPairedBefores } from '../../src/utils/postPairing';
import { FeedPostItem } from '@components/feed/FeedPostItem';
import { PendingObservations } from '@components/feed/PendingObservations';
import { FilterOption, buildFilters, FilterChips } from '@components/feed/FilterDropdown';
import { useScrollHideHeader } from '@hooks/useScrollHideHeader';
import { useVisibilityRefetch } from '@hooks/useVisibilityRefetch';
import { useNewItemIds } from '@hooks/useNewItemIds';

// How close to the top (px) the list has to be for a newly-arrived live
// post to auto-commit instead of parking behind the "N new posts" pill.
const NEW_POSTS_TOP_THRESHOLD = 200;

// Module-level so its identity never changes — useNewItemIds depends on it.
const getPostId = (post: Post) => post.id;

export default function FeedScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isDesktop } = useResponsiveLayout();
  const isDesktopShell = useDesktopShell();
  const user = useAuthStore((s) => s.user);
  const isModerator = useAuthStore((s) => s.isModerator);
  const openSheet = useUIStore((s) => s.openSheet);
  const { requireAuth } = useAuthGate();
  const confirm = useConfirm();

  // Narrow selectors: the bare usePostsStore() form re-rendered the whole
  // feed on every store change, including background mapPosts pushes from
  // the live map subscription the feed never reads.
  const {
    posts,
    userPosts,
    isLoading,
    isRefreshing,
    isFeedReady,
    hasMore,
    userPostsHasMore,
    isLoadingMoreUserPosts,
    error,
    likedPosts,
    pendingFeedPosts,
  } = usePostsStore(
    useShallow((s) => ({
      posts: s.posts,
      userPosts: s.userPosts,
      isLoading: s.isLoading,
      isRefreshing: s.isRefreshing,
      isFeedReady: s.isFeedReady,
      hasMore: s.hasMore,
      userPostsHasMore: s.userPostsHasMore,
      isLoadingMoreUserPosts: s.isLoadingMoreUserPosts,
      error: s.error,
      likedPosts: s.likedPosts,
      pendingFeedPosts: s.pendingFeedPosts,
    }))
  );
  const {
    fetchFeed,
    fetchFeedIfStale,
    fetchMorePosts,
    fetchUserPosts,
    fetchMoreUserPosts,
    likePost,
    unlikePost,
    checkFeedLikes,
    clearError,
    deletePost,
    subscribeFeed,
    unsubscribeFeed,
    commitPendingFeedPosts,
  } = usePostsStore(
    useShallow((s) => ({
      fetchFeed: s.fetchFeed,
      fetchFeedIfStale: s.fetchFeedIfStale,
      fetchMorePosts: s.fetchMorePosts,
      fetchUserPosts: s.fetchUserPosts,
      fetchMoreUserPosts: s.fetchMoreUserPosts,
      likePost: s.likePost,
      unlikePost: s.unlikePost,
      checkFeedLikes: s.checkFeedLikes,
      clearError: s.clearError,
      deletePost: s.deletePost,
      subscribeFeed: s.subscribeFeed,
      unsubscribeFeed: s.unsubscribeFeed,
      commitPendingFeedPosts: s.commitPendingFeedPosts,
    }))
  );

  const params = useLocalSearchParams<{ filter?: string }>();
  const initialFilter: FilterOption = params.filter === 'mine' && user ? 'mine' : 'all';
  const [filter, setFilter] = useState<FilterOption>(initialFilter);

  const { headerTranslateY, measuredHeaderHeight, onHeaderLayout, handleScroll } =
    useScrollHideHeader({ isDesktopShell });
  const listTopInset = measuredHeaderHeight + (isDesktopShell ? SPACING.lg : 0);

  const listRef = useRef<FlatList<Post>>(null);
  // Whether the list is within NEW_POSTS_TOP_THRESHOLD px of the top. Kept as
  // state (not just a ref) because it drives the pill's visibility and the
  // auto-commit effect below, but only flips at the threshold crossing —
  // not on every scroll frame — so it doesn't re-render per frame.
  const [isNearTop, setIsNearTop] = useState(true);

  // Refetch feed when the tab gains focus so new posts from other
  // users/tabs appear without a manual pull-to-refresh. Staleness-gated:
  // rapid tab switches reuse the cached feed instead of re-billing a full
  // page + like checks every focus. Also (un)subscribe the live feed head
  // here so it's only connected while the tab is actually focused.
  useFocusEffect(
    useCallback(() => {
      fetchFeedIfStale();
      subscribeFeed();
      return () => {
        unsubscribeFeed();
      };
    }, [fetchFeedIfStale, subscribeFeed, unsubscribeFeed])
  );

  // iPhone PWA gap: useFocusEffect only re-fires on in-app router
  // navigation, not on the OS/browser bringing an already-mounted tab back
  // to the front (bfcache restore, tab-switch back, alt-tab). fetchFeed(true)
  // is the pull-to-refresh path, so it also reconciles like-state and the
  // paginated tail the live head above doesn't cover. Guarded on isFeedReady
  // so a resume-ish event firing before the very first fetch (e.g. the
  // initial `pageshow` on page load itself) doesn't double up with that
  // first fetch; the isLoading/isRefreshing guard covers the in-flight case
  // fetchFeed itself doesn't check.
  const handleResume = useCallback(() => {
    const state = usePostsStore.getState();
    if (!state.isFeedReady || state.isLoading || state.isRefreshing) return;
    fetchFeed(true);
  }, [fetchFeed]);
  useVisibilityRefetch(handleResume);

  useEffect(() => {
    if (user) fetchUserPosts(user.id);
  }, [user, fetchUserPosts]);

  // New posts discovered by the live feed subscription: fold them straight
  // in while the viewer is at the top (seamless), otherwise leave them in
  // pendingFeedPosts for the pill below to surface on tap.
  useEffect(() => {
    if (isNearTop && pendingFeedPosts.length > 0) {
      commitPendingFeedPosts();
    }
  }, [isNearTop, pendingFeedPosts.length, commitPendingFeedPosts]);

  const handleFeedScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      handleScroll(event);
      const y = event.nativeEvent.contentOffset.y;
      const nearTop = y <= NEW_POSTS_TOP_THRESHOLD;
      setIsNearTop((prev) => (prev === nearTop ? prev : nearTop));
    },
    [handleScroll]
  );

  const handleCommitPendingFeedPosts = useCallback(() => {
    commitPendingFeedPosts();
    listRef.current?.scrollToOffset({ offset: 0, animated: true });
  }, [commitPendingFeedPosts]);

  // Check like statuses after posts load
  useEffect(() => {
    if (user && posts.length > 0) {
      checkFeedLikes(user.id);
    }
  }, [user, posts.length, checkFeedLikes]);

  const onRefresh = useCallback(() => {
    if (filter === 'mine' && user) {
      fetchUserPosts(user.id);
    } else {
      fetchFeed(true);
    }
  }, [filter, user, fetchFeed, fetchUserPosts]);

  const onEndReached = useCallback(() => {
    if (filter === 'mine') {
      if (user && userPostsHasMore && !isLoadingMoreUserPosts) {
        fetchMoreUserPosts(user.id);
      }
      return;
    }
    if (hasMore && !isLoading) {
      fetchMorePosts();
    }
  }, [
    filter,
    user,
    userPostsHasMore,
    isLoadingMoreUserPosts,
    fetchMoreUserPosts,
    hasMore,
    isLoading,
    fetchMorePosts,
  ]);

  // Keep latest likedPosts in a ref so handleLike stays stable across renders.
  const likedPostsRef = useRef(likedPosts);
  useEffect(() => {
    likedPostsRef.current = likedPosts;
  }, [likedPosts]);

  const handleLike = useCallback(
    (postId: string) => {
      requireAuth(
        () => {
          const uid = useAuthStore.getState().user!.id;
          (likedPostsRef.current.has(postId) ? unlikePost : likePost)(postId, uid);
        },
        { message: 'Sign in to like posts' }
      );
    },
    [requireAuth, likePost, unlikePost]
  );

  const handleOpenPost = useCallback(
    (postId: string) =>
      requireAuth(() => router.push(`/post/${postId}`), {
        message: 'Sign in to view post details',
        allowAnonymous: true,
      }),
    [requireAuth]
  );

  const handleOpenComments = useCallback(
    (postId: string) =>
      requireAuth(() => router.push(`/post/${postId}?openComments=1`), {
        message: 'Sign in to view comments',
        allowAnonymous: true,
      }),
    [requireAuth]
  );

  const handleEditPost = useCallback(
    (postId: string) => requireAuth(() => router.push(`/post/create?editPostId=${postId}`)),
    [requireAuth]
  );

  const handleDeletePost = useCallback(
    (postId: string) => {
      requireAuth(async () => {
        const post = posts.find((p) => p.id === postId) || userPosts.find((p) => p.id === postId);
        if (!post) return;
        const confirmed = await confirm({
          title: 'Delete post?',
          message: 'This action cannot be undone.',
          confirmLabel: 'Delete',
          destructive: true,
        });
        if (confirmed) await deletePost(post.id, post.authorId, post.structureId).catch(() => {});
      });
    },
    [requireAuth, posts, userPosts, confirm, deletePost]
  );

  // Known-to-the-client posts for before/after suppression: broader than any
  // single rendered list so a before post is hidden the moment its after is
  // known anywhere — including a pending live-head after that hasn't been
  // committed into `posts` yet (see postPairing.ts).
  const knownPosts = useMemo(
    () => [...posts, ...pendingFeedPosts, ...userPosts],
    [posts, pendingFeedPosts, userPosts]
  );

  // userPosts is the profile's generic "my posts" cache (any category,
  // fetched once for the whole app) — this screen is news-only, so it's
  // narrowed to the viewer's own news posts before it ever reaches the
  // merge or the "My news" filter below.
  const ownNewsPosts = useMemo(() => userPosts.filter((p) => p.category === 'news'), [userPosts]);

  // The base feed query is visibility-filtered at the rules layer, so the
  // signed-in user's own private/insurance news posts come from
  // ownNewsPosts (already fetched for the "My news" filter) and merge in
  // within the loaded window.
  const filteredPosts = useMemo(() => {
    const mergedPosts = user ? mergeOwnPosts(posts, ownNewsPosts, hasMore) : posts;
    const base = filter === 'mine' ? ownNewsPosts : mergedPosts;
    return suppressPairedBefores(base, knownPosts);
  }, [user, posts, ownNewsPosts, hasMore, filter, knownPosts]);

  // Web-only enter animation for rows inserted above the rest of the feed
  // (a live post arriving, or one the viewer just created) — see
  // useNewItemIds.ts for why pagination and filter switches don't animate.
  const newPostIds = useNewItemIds(filteredPosts, getPostId);

  const renderItem = useCallback(
    ({ item }: { item: (typeof posts)[number] }) => (
      <FeedPostItem
        post={item}
        isLiked={likedPosts.has(item.id)}
        containerStyle={styles.listItem}
        currentUserId={user?.id}
        onOpen={handleOpenPost}
        onLike={handleLike}
        onOpenComments={handleOpenComments}
        onEdit={handleEditPost}
        onDelete={handleDeletePost}
        animateIn={newPostIds.has(item.id)}
      />
    ),
    [
      likedPosts,
      user?.id,
      handleOpenPost,
      handleLike,
      handleOpenComments,
      handleEditPost,
      handleDeletePost,
      newPostIds,
    ]
  );

  if (error && posts.length === 0) {
    return (
      <View
        style={[styles.container, { backgroundColor: colors.background, paddingTop: insets.top }]}>
        <ErrorState
          message={error}
          onRetry={() => {
            clearError();
            fetchFeed();
          }}
        />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>News - Redi</title>
      </Head>
      {/* Header — absolutely positioned, hides on scroll down */}
      <Animated.View
        onLayout={onHeaderLayout}
        style={[
          styles.header,
          isDesktopShell && styles.headerDesktop,
          {
            // Desktop puts the top inset inside the title band below, which
            // owns the rule; on mobile it belongs to the whole block.
            paddingTop: isDesktopShell ? 0 : insets.top + 8,
            backgroundColor: colors.background,
            borderBottomColor: colors.border,
            transform: [{ translateY: headerTranslateY }],
          },
        ]}>
        {/* Desktop: the title sits in a fixed band whose rule lines up with
            the sidebar's divider, and the filter chips render below that rule
            as content. They used to sit above it, which pushed this screen's
            rule to y=91 while the sidebar's sat at 72. Mobile has no sidebar
            to line up with and keeps one bordered block. */}
        <View
          style={
            isDesktopShell ? [styles.titleBand, { borderBottomColor: colors.border }] : undefined
          }>
          <View
            style={[
              styles.headerRow,
              isDesktopShell && styles.headerRowDesktop,
              isDesktopShell && styles.headerRowDesktopBand,
            ]}>
            {/* The sidebar already carries the wordmark on desktop — this is a
              page header, not app chrome. */}
            {isDesktopShell ? (
              <View style={styles.desktopTitleRow}>
                <Text style={[styles.desktopTitle, { color: colors.text }]}>News</Text>
                {isModerator && (
                  <DotLabel label="Moderator" variant="primary" testID="moderator-chip" />
                )}
              </View>
            ) : (
              <RediLogo />
            )}
            <View style={styles.headerActions}>
              {!isDesktopShell && isModerator && (
                <DotLabel label="Moderator" variant="primary" testID="moderator-chip" />
              )}
              {/* Desktop shell: the sidebar footer owns the user entry point. */}
              {!isDesktopShell &&
                (user ? (
                  <TouchableOpacity
                    onPress={() => openSheet('userMenu')}
                    accessibilityRole="button"
                    accessibilityLabel="Open user menu"
                    hitSlop={8}>
                    <Avatar
                      size={32}
                      name={`${user.firstName} ${user.lastName}`}
                      imageUrl={user.avatarUrl}
                    />
                  </TouchableOpacity>
                ) : (
                  <SignInChip variant="bare" />
                ))}
            </View>
          </View>
        </View>

        {/* Big light screen title (Plaud grammar) — the sidebar-less mobile
            header carries it; desktop shows its own title above. */}
        {!isDesktopShell && <Text style={[styles.screenTitle, { color: colors.text }]}>News</Text>}

        {/* Permanent filter tabs — the Progress/Risk lens is core to reading
            the feed, so it stays in the open. Hides with the header on scroll.
            Desktop shell renders these as list content instead (see
            ListHeaderComponent): there the rule sits at SHELL_HEADER_HEIGHT to
            line up with the sidebar, and anything pinned below that rule would
            leave cards clipped at the header's real bottom edge with no line
            to mask the cut. */}
        {!isDesktopShell && (
          <View style={styles.filterRow}>
            {/* The header's own bottom border is the tabs' hairline rule —
                zero the TextTabs one so the lines don't double up. */}
            <FilterChips
              filter={filter}
              setFilter={setFilter}
              filters={buildFilters(!!user)}
              style={styles.filterTabs}
            />
          </View>
        )}
      </Animated.View>

      {/* Feed */}
      <View style={styles.body}>
        <ContentConstraint wide>
          {/* The header is absolute, so the list clears it with its own top
              padding. On the desktop shell that put the first observation
              hard against the filter tabs' hairline: on a phone the card's
              own fill separates them, but the ported card is on canvas and
              has no edge of its own. Skeleton and list must use the same
              value or the feed jumps as it loads. */}
          {!isFeedReady ? (
            // News is a single reading column everywhere (text-first entries,
            // Aug 2026) — the skeleton mirrors that one shape.
            <View
              style={[
                styles.skeletonContainer,
                { paddingTop: listTopInset },
                isDesktopShell && styles.readingColumn,
              ]}>
              {Array.from({ length: 4 }).map((_, row) => (
                <View key={row} style={styles.listItem}>
                  <PostCardSkeleton variant="news" />
                </View>
              ))}
            </View>
          ) : (
            <FlatList
              ref={listRef}
              data={filteredPosts}
              keyExtractor={(item) => item.id}
              renderItem={renderItem}
              ListHeaderComponent={
                <>
                  {/* Desktop shell: the chips scroll with the feed rather than
                      sitting pinned under the header rule. */}
                  {isDesktopShell && (
                    <View style={styles.filterRowDesktop}>
                      <FilterChips
                        filter={filter}
                        setFilter={setFilter}
                        filters={buildFilters(!!user)}
                        style={styles.filterTabs}
                      />
                    </View>
                  )}
                  {/* One gutter on every layout — news entries inset their
                      own text by the same amount. */}
                  <PendingObservations style={styles.pendingObservations} />
                </>
              }
              removeClippedSubviews
              initialNumToRender={8}
              maxToRenderPerBatch={8}
              windowSize={7}
              onScroll={handleFeedScroll}
              scrollEventThrottle={16}
              refreshControl={
                <RefreshControl
                  refreshing={isRefreshing}
                  onRefresh={onRefresh}
                  tintColor={colors.textSecondary}
                  progressViewOffset={measuredHeaderHeight}
                />
              }
              onEndReached={onEndReached}
              onEndReachedThreshold={0.5}
              contentContainerStyle={[
                { paddingBottom: 100, paddingTop: listTopInset },
                isDesktop && styles.gridContainer,
                // Desktop shell: one centered reading column, not a grid —
                // text entries at pane width read as a wall of prose.
                isDesktopShell && styles.readingColumn,
                filteredPosts.length === 0 && styles.emptyContainer,
              ]}
              ListEmptyComponent={
                isLoading || isRefreshing ? null : (
                  <EmptyState
                    icon={Newspaper}
                    title="No news yet"
                    message={
                      filter === 'mine'
                        ? 'News you share will appear here.'
                        : "Share what is happening in your community's wildfire preparation."
                    }
                    actionLabel="Share News"
                    onAction={() =>
                      requireAuth(() => router.push('/post/create'), {
                        message: 'Sign in to create a post',
                      })
                    }
                  />
                )
              }
              showsVerticalScrollIndicator={false}
            />
          )}
        </ContentConstraint>

        {/* Live-feed "new posts" pill: only while pending posts exist AND the
            viewer is scrolled away from the top (near-top arrivals commit
            automatically — see the isNearTop effect above). Sits just below
            the filter chips, clear of both the header content and the
            floating tab bar at the bottom. */}
        {!isNearTop && pendingFeedPosts.length > 0 && (
          <View
            pointerEvents="box-none"
            style={[styles.pillWrapper, { top: measuredHeaderHeight + SPACING.sm }]}>
            <TouchableOpacity
              onPress={handleCommitPendingFeedPosts}
              style={[styles.pill, { backgroundColor: colors.action }, ELEVATION.floating]}
              accessibilityRole="button"
              accessibilityLabel={`Show ${pendingFeedPosts.length} new post${pendingFeedPosts.length === 1 ? '' : 's'}`}
              testID="feed-new-posts-pill">
              <ArrowUp size={14} color={colors.actionOn} />
              <Text style={[styles.pillText, { color: colors.actionOn }]}>
                {pendingFeedPosts.length} new post{pendingFeedPosts.length === 1 ? '' : 's'}
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  body: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    borderBottomWidth: 1,
    ...ELEVATION.sm,
  },
  // Desktop: the 1px border contains the header; a drop shadow on pinned
  // desktop chrome reads as floating mobile chrome. The rule moves onto the
  // title band inside, so the outer block carries none.
  headerDesktop: {
    shadowOpacity: 0,
    elevation: 0,
    borderBottomWidth: 0,
  },
  // The fixed band the rule hangs off, so this screen's line lands on the
  // same y as the sidebar's divider. Border-box, so the rule paints the row
  // at SHELL_HEADER_HEIGHT. Full width: the rule spans the pane even though
  // the title inside is inset to the grid gutter.
  titleBand: {
    height: SHELL_HEADER_HEIGHT + SHELL_RULE_WIDTH,
    borderBottomWidth: SHELL_RULE_WIDTH,
    paddingTop: SPACING.lg,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACING.md,
    paddingBottom: SPACING.sm,
  },
  filterRow: {
    paddingHorizontal: SPACING.md,
  },
  // List content on the desktop shell, below the rule. One gutter here plus
  // the content container's own padding lines the chips up with the news
  // entries' text (each entry carries SPACING.md internal padding).
  filterRowDesktop: {
    paddingHorizontal: SPACING.md,
    paddingBottom: SPACING.sm,
  },
  filterTabs: {
    borderBottomWidth: 0,
  },
  screenTitle: {
    ...TYPOGRAPHY.screenTitle,
    paddingHorizontal: SPACING.md,
    paddingBottom: SPACING.smd,
  },
  headerRowDesktop: {
    width: '100%',
    // The list below centers on the reading column (READING_MAX_WIDTH), not
    // the wider grid — this used to center on DESKTOP_CONTENT_MAX_WIDTH
    // instead, landing the title well left of the cards under it (Sept 29
    // 2026, F1).
    maxWidth: READING_MAX_WIDTH,
    alignSelf: 'center',
    // The reading column's own gutter (SPACING.md, via gridContainer) plus
    // the card's internal padding (SPACING.md). Match that here so the
    // title and filter tabs line up with the card content, not the
    // invisible column edge.
    paddingHorizontal: SPACING.md + SPACING.md,
  },
  // Inside the fixed band the row's bottom padding would push the title up
  // off the shell's 24px top rhythm.
  headerRowDesktopBand: {
    paddingBottom: 0,
  },
  // Baseline, not center: the badge sat high beside the tall title
  // (Sept 28).
  desktopTitleRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: SPACING.sm,
  },
  desktopTitle: {
    ...TYPOGRAPHY.screenTitle,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
  },
  skeletonContainer: {
    flex: 1,
  },
  pendingObservations: {
    paddingHorizontal: SPACING.md,
  },
  emptyContainer: {
    flex: 1,
  },
  gridContainer: {
    padding: SPACING.md,
  },
  // The news reading column on the desktop shell: entries at full pane width
  // read as a wall of prose, so the whole list constrains to ~65ch and
  // centers. The header chips and pending strip ride inside it too
  // (ListHeaderComponent renders within the content container).
  readingColumn: {
    width: '100%',
    maxWidth: 680,
    alignSelf: 'center',
  },
  listItem: {
    flex: 1,
  },
  pillWrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    // Above the header's own zIndex (100) so it's never obscured while the
    // header is hiding/showing on scroll.
    zIndex: 150,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.full,
  },
  pillText: {
    ...TYPOGRAPHY.label,
  },
});
